import { useLocaleStore } from './locale-store';

export type Locale = 'en' | 'ko';

const cache: Record<Locale, Record<string, string>> = { en: {}, ko: {} };
const loaded: Record<Locale, boolean> = { en: false, ko: false };

function cacheKeyName(locale: Locale, hash: string): string {
  return `rb_admin_i18n_${locale}_${hash}`;
}

/**
 * Mirrors the game client's i18n loader (apps/client/src/i18n/index.ts):
 *
 * 1. Fetch `<base>/l10n/manifest.json` — returns `{ en: hash, ko: hash }`.
 * 2. Look up cached translations by `rb_admin_i18n_<locale>_<hash>` (hash in
 *    the key invalidates stale entries when the CDN hash changes).
 * 3. On miss, fetch `<base>/l10n/<locale>.<hash>.json` and persist.
 *
 * After successful load, bumps `useLocaleStore` `dataVersion` so subscribed
 * cells re-render — the `cache` itself is a module variable and not
 * reactive on its own.
 */
export async function loadTranslations(locale: Locale, cdnBase: string): Promise<void> {
  let hash = 'v0';
  try {
    const manifestRes = await fetch(`${cdnBase}/l10n/manifest.json`, { cache: 'no-store' });
    if (manifestRes.ok) {
      const manifest = (await manifestRes.json()) as Record<string, string>;
      hash = manifest[locale] ?? 'v0';
    }
  } catch (err) {
    console.warn(`[translations] manifest fetch failed:`, err);
  }

  const key = cacheKeyName(locale, hash);

  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        cache[locale] = parsed;
        loaded[locale] = true;
        useLocaleStore.getState().bumpDataVersion();
        return;
      }
    }
  } catch {
    localStorage.removeItem(key);
  }

  try {
    const res = await fetch(`${cdnBase}/l10n/${locale}.${hash}.json`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as Record<string, string>;
    cache[locale] = data;
    loaded[locale] = true;
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // quota exceeded — silently drop
    }
    useLocaleStore.getState().bumpDataVersion();
  } catch (err) {
    console.warn(`[translations] load ${locale} failed:`, err);
  }
}

export function translateKey(key: string, locale: Locale): string {
  return cache[locale][key] ?? key;
}

export function isLoaded(locale: Locale): boolean {
  return loaded[locale];
}

export function _resetCacheForTests() {
  cache.en = {};
  cache.ko = {};
  loaded.en = false;
  loaded.ko = false;
}
